'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

// mousemove is intentionally excluded: mouse drift (USB jitter, external
// monitor refresh, HID reports on some platforms) can fire continuously
// without any real user interaction, effectively disabling the timeout.
const IDLE_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
const CJIS_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes (CJIS 5.6.3.1)
const DEFAULT_WARNING_MS = 60 * 1000; // 60s pre-lock warning window
// Wall-clock reconciliation cadence. Bounds the window in which a
// background-throttled or frozen tab can stay out-of-sync with reality
// after the host resumes scheduling timers.
const RECONCILE_INTERVAL_MS = 10 * 1000;

// gh #877: cross-tab activity propagation. The idle timer is per-tab and a
// background tab receives none of IDLE_EVENTS, so it reliably goes idle in
// 30 min and fires lockSession() — which detonates the foreground tab the
// user is actively working in (shared Firebase IndexedDB + bearer blocklist).
// Broadcasting activity over this channel makes "idle" mean "no activity in
// ANY tab of this origin": any tab's real interaction (or active upload,
// which pulses markActive) resets the idle timer in all siblings. This is the
// CJIS-compliant fix — the user genuinely IS active, so the lock must not fire.
const ACTIVITY_CHANNEL_NAME = 'session-activity';
// Activity fires far more often than the 30-min timeout needs; one cross-tab
// pulse per this interval keeps siblings alive while keeping the channel quiet.
const ACTIVITY_BROADCAST_THROTTLE_MS = 5 * 1000;

export interface UseIdleTimeoutOptions {
  timeoutMs?: number;
  warningMs?: number;
  enabled?: boolean;
}

export function useIdleTimeout(options: UseIdleTimeoutOptions = {}) {
  const {
    timeoutMs = CJIS_IDLE_TIMEOUT_MS,
    warningMs = DEFAULT_WARNING_MS,
    enabled = true,
  } = options;
  const [isIdle, setIsIdle] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(0);
  // gh #877: cross-tab activity channel + broadcast throttle clock.
  const activityChannelRef = useRef<BroadcastChannel | null>(null);
  const lastBroadcastRef = useRef<number>(0);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
  }, []);

  // Wall-clock reconciliation. setTimeout is unreliable across tab
  // backgrounding (Chrome throttles to ~1Hz, then freezes/discards after
  // ~5min on battery) and laptop sleep (timers do not advance). Recompute
  // state from Date.now() - lastActivityRef so a tab returning from
  // background lands on the correct state instead of firing warn+idle
  // setTimeouts back-to-back -- which races the warning out of view since
  // providers.tsx gates the warning render on !showLockOverlay.
  const reconcile = useCallback(() => {
    const elapsed = Date.now() - lastActivityRef.current;
    const warnDelay = Math.max(0, timeoutMs - warningMs);

    if (elapsed >= timeoutMs) {
      clearTimers();
      setIsWarning(true);
      setIsIdle(true);
      return;
    }

    if (elapsed >= warnDelay) {
      setIsWarning(true);
      clearTimers();
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
      }, timeoutMs - elapsed);
      return;
    }

    clearTimers();
    warnTimerRef.current = setTimeout(() => {
      setIsWarning(true);
    }, warnDelay - elapsed);
    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true);
    }, timeoutMs - elapsed);
  }, [timeoutMs, warningMs, clearTimers]);

  // gh #877: tell sibling tabs "this tab just saw activity" so their idle
  // timers reset too. Throttled — activity is bursty and the 30-min timeout
  // does not need sub-5s resolution. Never called from the receive path, so a
  // remote pulse can never echo back into an infinite cross-tab loop.
  const broadcastActivity = useCallback(() => {
    const channel = activityChannelRef.current;
    if (!channel) return;
    const now = Date.now();
    if (now - lastBroadcastRef.current < ACTIVITY_BROADCAST_THROTTLE_MS) return;
    lastBroadcastRef.current = now;
    try {
      channel.postMessage({ type: 'activity', ts: now });
    } catch {
      // Channel closed mid-teardown; ignore.
    }
  }, []);

  const markActive = useCallback(() => {
    if (!enabled) return;
    lastActivityRef.current = Date.now();
    setIsIdle(false);
    setIsWarning(false);
    reconcile();
    // Propagate to siblings so an active upload (which pulses markActive via
    // onUploadActivity) or a programmatic keep-alive also holds off the lock
    // in background tabs, not just this one.
    broadcastActivity();
  }, [enabled, reconcile, broadcastActivity]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // Reset on (re-)enable so a stale isIdle=true from a previous session
    // (e.g. user re-authenticated after a lock) does not immediately re-fire
    // the lock overlay before the new 30-min timer has had a chance to run.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- CJIS 5.6.3.1: stale lock/warning-state reset on re-enable is intentional
    setIsIdle(false);
    setIsWarning(false);
    lastActivityRef.current = Date.now();
    reconcile();

    const handler = () => {
      lastActivityRef.current = Date.now();
      setIsIdle(false);
      setIsWarning(false);
      reconcile();
      broadcastActivity();
    };
    for (const event of IDLE_EVENTS) {
      window.addEventListener(event, handler, { passive: true });
    }

    // gh #877: cross-tab activity. Any sibling tab's real interaction resets
    // THIS tab's idle timer, so a background tab no longer locks the session
    // out from under an actively-used foreground tab. The receive path
    // deliberately does NOT re-broadcast (broadcastActivity is not called
    // here) so a single interaction can't ping-pong between tabs forever.
    if ('BroadcastChannel' in window) {
      try {
        const channel = new BroadcastChannel(ACTIVITY_CHANNEL_NAME);
        channel.onmessage = (ev) => {
          if (ev.data?.type !== 'activity') return;
          lastActivityRef.current = Date.now();
          setIsIdle(false);
          setIsWarning(false);
          reconcile();
        };
        activityChannelRef.current = channel;
      } catch {
        // BroadcastChannel unavailable; per-tab timer still works.
        activityChannelRef.current = null;
      }
    }

    // visibilitychange: tab regaining focus must re-evaluate from wall
    // clock, not from the possibly-throttled / out-of-order setTimeout
    // callbacks that the browser queued while we were hidden.
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        reconcile();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    // Heartbeat catches the case where a background tab was frozen across
    // the warn+lock thresholds: on resume the next interval tick fires
    // promptly and reconcile() pushes us to the correct state within
    // RECONCILE_INTERVAL_MS instead of waiting for the next user event.
    heartbeatRef.current = setInterval(reconcile, RECONCILE_INTERVAL_MS);

    return () => {
      for (const event of IDLE_EVENTS) {
        window.removeEventListener(event, handler);
      }
      document.removeEventListener('visibilitychange', visibilityHandler);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (activityChannelRef.current) {
        activityChannelRef.current.close();
        activityChannelRef.current = null;
      }
      clearTimers();
    };
  }, [enabled, timeoutMs, warningMs, reconcile, clearTimers, broadcastActivity]);

  return { isIdle, isWarning, markActive, lastActivityRef };
}
